import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    EMPTY,
    Observable,
    catchError,
    finalize,
    shareReplay,
    switchMap,
    throwError
} from 'rxjs';
import { TokenResponse } from 'src/app/models/refresh-token.model';
import { AuthInterceptorHelper } from 'src/app/services/auth-interceptor-helper/auth-interceptor-helper';
import { AuthService } from 'src/app/services/auth/auth.service';
import { VehicleOrderProvider } from 'src/app/services/providers/vehicle-order-provider/vehicle-order-provider';

@Injectable()
export class RefreshInterceptor implements HttpInterceptor {
    private refreshRequest$: Observable<TokenResponse> | null = null;

    constructor(
        private readonly authInterceptorHelper: AuthInterceptorHelper,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly vehicleOrderProvider: VehicleOrderProvider
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                const isRefreshApi = req.url.includes('auth/jwt/refresh');
                const isTokenExpired =
                    error.status === 401 &&
                    error.error?.message === 'TokenExpired';

                if (isTokenExpired && !isRefreshApi) {
                    this.vehicleOrderProvider.clearMetadata();
                    return this.handleTokenExpired(req, next);
                }

                return throwError(() => error);
            })
        );
    }

    private handleTokenExpired(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return this.getSharedRefreshRequest().pipe(
            switchMap((response: TokenResponse) => {
                const newAccessToken = response.accessToken.token;

                const retryReq = this.authInterceptorHelper.addAuthenticationToken(
                    req,
                    newAccessToken
                );

                return next.handle(retryReq);
            }),
            catchError(() => {
                this.authInterceptorHelper.logout(true, false);
                this.router.navigateByUrl('/error?type=session-expired');
                return EMPTY;
            })
        );
    }

    private getSharedRefreshRequest(): Observable<TokenResponse> {
        this.refreshRequest$ ??= this.authService.processRefreshToken().pipe(
            finalize(() => {
                this.refreshRequest$ = null;
            }),
            shareReplay({
                bufferSize: 1,
                refCount: false
            })
        );

        return this.refreshRequest$;
    }
}
